/* ── mapTheme ────────────────────────────────────────────────
 * Dark-mode cartography for the Google base map.
 *
 * Google's map style JSON is a third-party data format that only
 * accepts literal colors — it cannot reference CSS custom properties.
 * So we read the tokens off the document at runtime and hand Google
 * the computed values. The map therefore still follows tokens.scss
 * instead of carrying a private palette that would drift the moment
 * the theme changed.
 * ──────────────────────────────────────────────────────────── */

/**
 * Every token below is defined in tokens.scss. `fallback` only matters
 * if this somehow runs before the stylesheet applies, in which case
 * Google would otherwise receive an empty string and reject the whole
 * style array.
 */
const token = (name, fallback) => {
  if (typeof window === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return v || fallback;
};

export const isDarkTheme = () =>
  typeof document !== 'undefined' &&
  document.documentElement.getAttribute('data-theme') === 'dark';

export const buildDarkStyles = () => {
  const surface = token('--color-surface-2', '#141414');
  const body = token('--color-text-body', '#C9C9C9');
  const meta = token('--color-text-meta', '#8A8A8A');
  const border = token('--color-border', '#2C2C2C');
  const water = token('--color-map-water', '#0E1A24');

  return [
    { elementType: 'geometry', stylers: [{ color: surface }] },
    { elementType: 'labels.text.fill', stylers: [{ color: body }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: surface }] },
    { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: meta }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: border }] },
    { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: meta }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: water }] },
    { featureType: 'transit', elementType: 'geometry', stylers: [{ color: border }] },
  ];
};

/** Styles for the current theme, or null to clear back to Google's default. */
export const stylesForTheme = () => (isDarkTheme() ? buildDarkStyles() : null);
